import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  addMealPlanAction,
  deleteMealPlanAction,
  updateMealPlanAction,
} from "./actions";
import { MessagesType } from "@/utils/types/mealplan";
import { MealPlanDataItem } from "@/utils/types/recipes";

export interface MealPlanItem {
  user_id: string;
  meal_type_id: number;
  schedule_date: string;
  mealplanRecipeList: MealPlanDataItem[];
  weekDate: any;
  selectedMealPlans: any;
  selectedListRecipes: any;
  selectedCalendarDate: string;
  visibleDateRange: any;
}

export interface RecipeItem {
  recipe_id: string;
}
const initialState: MealPlanItem = {
  user_id: "",
  meal_type_id: 0,
  schedule_date: "",
  mealplanRecipeList: [],
  weekDate: [],
  selectedMealPlans: [],
  selectedListRecipes: [],
  selectedCalendarDate: "",
  visibleDateRange: {
    startDate: null,
    endDate: null,
  },
};

const initialMessage: MessagesType = {
  MEAL_PLAN_ADDED: "",
  MEAL_PLAN_REMOVED: "",
  EMAIL_SENT: "",
  MEAL_PLAN_COPIED: "",
  ERROR_MESSAGE: {
    MEAL_PLAN_NOT_ADDED: "",
    MEAL_PLAN_ALREADY_EXIST: "",
    MEAL_PLAN_NOT_REMOVED: "",
    MEAL_PLAN_FETCH_FAILED: "",
    OFFLINE_ERROR: "",
    SERVER_ERROR: "",
    DATA_NOT_FOUND: "",
    NO_RECIPE_FOUND: "",
    NO_RECIPE_LIST_FOUND: "",
    MEAL_PLAN_COPY_FAILED: "",
    INVALID_EMAIL: "",
    MEAL_PLAN_LIMIT_EXCEED: "",
    MEAL_EXCEED_TITLE: "",
  },
};

const mealPlanSlice = createSlice({
  name: "mealPlan",
  initialState,
  reducers: {
    setMealPlanData: (state, action: PayloadAction<MealPlanDataItem[]>) => {
      state.mealplanRecipeList = action.payload;
    },
    updateMealPlanData: (
      state,
      action: PayloadAction<{ id: string | number; schedule_date: string }>
    ) => {
      const { id, schedule_date } = action.payload;

      // Convert id to string for consistent comparison
      const targetId = String(id);

      const index = state.mealplanRecipeList.findIndex(
        (item: MealPlanDataItem) => item.id === targetId
      );

      if (index !== -1) {
        state.mealplanRecipeList[index].schedule_date = schedule_date;
      }
    },
    setCurrentWeekAction: (
      state,
      action: PayloadAction<MealPlanItem["weekDate"]>
    ) => {
      state.weekDate = action?.payload;
    },
    updateSeletedMealPlan: (
      state,
      action: PayloadAction<MealPlanItem | MealPlanItem[]>
    ) => {
      state.selectedMealPlans = action?.payload;
    },
    setSelectedMealPlansAction: (
      state,
      action: PayloadAction<MealPlanItem | MealPlanItem[]>
    ) => {
      const payload: any = action.payload;

      // If passed an empty array, clear the selected list
      if (Array.isArray(payload) && payload.length === 0) {
        state.selectedMealPlans = [];
        return;
      }
      // If passed a single MealPlanItem
      if (!Array.isArray(payload)) {
        const exists = state.selectedMealPlans?.some(
          (item: any) => item?.id === payload.id
        );
        if (exists) {
          state.selectedMealPlans = state.selectedMealPlans?.filter(
            (item: any) => item?.id !== payload.id
          );
        } else {
          state.selectedMealPlans.push(payload);
        }
      }
    },

    selectedListRecipesAction: (
      state,
      action: PayloadAction<MealPlanItem["selectedListRecipes"]>
    ) => {
      state.selectedListRecipes = action?.payload;
    },
    selectedCalendarDateAction: (state, action: PayloadAction<any>) => {
      state.selectedCalendarDate = action?.payload;
    },
    setVisibleDateRangeAction: (state, action) => {
      state.visibleDateRange = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      addMealPlanAction.fulfilled,
      (state, action: PayloadAction<any>) => {}
    );
    builder.addCase(
      updateMealPlanAction.fulfilled,
      (state, action: PayloadAction<any>) => {}
    );
    builder.addCase(
      deleteMealPlanAction.fulfilled,
      (state, action: PayloadAction<any>) => {
        const deletedIds =
          action?.payload?.deleted?.map((id: any) => Number(id)) || [];
        //Remove items from state.mealplanRecipeList where id matches any in deletedIds
        state.mealplanRecipeList = state.mealplanRecipeList.filter(
          (item: any) => !deletedIds.includes(Number(item.id))
        );

        //Remove items from state.selectedMealPlans where id matches any in deletedIds
        state.selectedMealPlans = state.selectedMealPlans.filter(
          (item: any) => !deletedIds.includes(Number(item.id))
        );
      }
    );
  },
});

const messages = createSlice({
  name: "mealPlanMessages",
  initialState: {
    messages: initialMessage,
  },
  reducers: {
    setMessages(state: any, action: PayloadAction<any>) {
      const {
        mealPlanAdded,
        mealPlanRemoved,
        mealPlanNotAdded,
        serverError,
        dataNotFound,
        noMealPlanRecipeListFound,
        noRecipeFound,
        emailSent,
        mealPlanNotRemoved,
        mealPlanFetchFailed,
        mealPlanCopied,
        mealPlanCopyFailed,
        invalidEmail,
        offlineError,
        mealPlanAlreadyExists,
        limitExceededMessage,
        limitExceededTitle,
      } = action.payload;

      const message = {
        MEAL_PLAN_ADDED: mealPlanAdded,
        MEAL_PLAN_REMOVED: mealPlanRemoved,
        EMAIL_SENT: emailSent,
        MEAL_PLAN_COPIED: mealPlanCopied,

        ERROR_MESSAGE: {
          MEAL_PLAN_NOT_ADDED: mealPlanNotAdded,
          MEAL_PLAN_ALREADY_EXIST: mealPlanAlreadyExists,
          OFFLINE_ERROR: offlineError,
          SERVER_ERROR: serverError,
          MEAL_PLAN_NOT_REMOVED: mealPlanNotRemoved,
          MEAL_PLAN_FETCH_FAILED: mealPlanFetchFailed,
          MEAL_PLAN_COPY_FAILED: mealPlanCopyFailed,
          INVALID_EMAIL: invalidEmail,
          NO_RECIPE_FOUND: noRecipeFound,
          NO_RECIPE_LIST_FOUND: noMealPlanRecipeListFound,
          DATA_NOT_FOUND: dataNotFound,
          MEAL_PLAN_LIMIT_EXCEED: limitExceededMessage,
          MEAL_EXCEED_TITLE: limitExceededTitle,
        },
      };

      state.messages = message;
    },
  },
});

export const {
  setMealPlanData,
  updateMealPlanData,
  setCurrentWeekAction,
  setSelectedMealPlansAction,
  selectedListRecipesAction,
  selectedCalendarDateAction,
  setVisibleDateRangeAction,
  updateSeletedMealPlan,
} = mealPlanSlice.actions;
export const mealPlanReducer = mealPlanSlice.reducer;

export const { setMessages } = messages.actions;

export const mealPlanMessageSlice = messages.reducer;
